import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Building2, Trash2, Loader2, CheckCircle2, UploadCloud, Download, Clock, LogIn, ShieldAlert, FileEdit, ScrollText, SlidersHorizontal, RefreshCw } from "lucide-react";
import { useModules } from "@/hooks/useModules";
import { TemplateEditor } from "./TemplateEditor";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { Badge, PillSelect, Panel } from "@/components/kit";
import { initials } from "@/components/shell/AppShell";
import api, { apiError } from "@/lib/api";


const PanelHead = ({ title, sub, right }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h2 className="font-heading text-2xl font-extrabold tracking-tight text-thstrong">{title}</h2>
      <p className="mt-1 max-w-2xl text-sm text-thmute">{sub}</p>
    </div>
    {right}
  </div>
);

const TenantSelect = ({ testid }) => {
  const { tenants, tenantId, switchTenant } = useTenant();
  return (
    <PillSelect testid={testid} icon={Building2} value={tenantId || ""} onChange={(e) => switchTenant(e.target.value)}>
      {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </PillSelect>
  );
};

// ---------- Clients ----------
export const ClientsPanel = () => {
  const { user } = useAuth();
  const { tenants, refreshTenants } = useTenant();
  const [name, setName] = useState("");
  const isSuper = user?.role === "super_admin";

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tenants", { name });
      toast.success(`Client "${name}" created`);
      setName("");
      refreshTenants();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Remove client "${t.name}"? Uploads and permissions for it will be deleted.`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success("Client removed");
      refreshTenants();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PanelHead title="Clients" sub="Each client (tenant) has its own monthly uploads and permissions. Data always stays inside the client's workspace." />
      {isSuper && (
        <form onSubmit={add} className="mb-6 flex max-w-md gap-3">
          <input data-testid="client-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New client name" required className="mk-input" />
          <button data-testid="client-add-btn" className="mk-btn-primary shrink-0">Add Client</button>
        </form>
      )}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {tenants.map((t, i) => (
          <div key={t.id} className="mk-card mk-fade flex items-center justify-between p-5" style={{ animationDelay: `${i * 50}ms` }} data-testid={`client-card-${t.name}`}>
            <div className="flex items-center gap-3">
              <span className="mk-icon-tile h-10 w-10 rounded-xl bg-thblue/12 text-thblue"><Building2 className="h-4 w-4" /></span>
              <div>
                <p className="text-sm font-semibold text-thstrong">{t.name}</p>
                <p className="mk-sub">Workspace</p>
              </div>
            </div>
            {isSuper && (
              <button data-testid={`client-delete-${t.name}`} onClick={() => remove(t)} className="text-thdim transition-colors hover:text-thcoral"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
        {tenants.length === 0 && <p className="text-sm text-thdim">No clients yet{isSuper ? " — add the first one above." : "."}</p>}
      </div>
    </div>
  );
};

// ---------- Permissions ----------
export const PermissionsPanel = () => {
  const { tenantId } = useTenant();
  const { modules } = useModules();
  const MODULES = (modules || []).map((m) => [m.slug, m.name]);
  const [perms, setPerms] = useState(null);

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/permissions?tenant_id=${tenantId}`).then((r) => setPerms(r.data));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (slug) => {
    const cur = perms.user || [];
    const next = cur.includes(slug) ? cur.filter((m) => m !== slug) : [...cur, slug];
    try {
      await api.put("/permissions", { tenant_id: tenantId, role: "user", modules: next });
      setPerms({ ...perms, user: next });
      toast.success("Permissions saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PanelHead title="Permissions" sub="Per-role module access for each client. Admins and Super Admins always see all modules." right={<TenantSelect testid="perm-tenant-select" />} />
      {!tenantId ? <p className="text-sm text-thdim">Create a client first.</p> : !perms ? <Loader2 className="h-6 w-6 animate-spin text-thcyan" /> : (
        <div className="mk-card overflow-x-auto">
          <table className="mk-table" data-testid="perm-matrix">
            <thead><tr>
              <th className="pl-5">Role</th>
              {MODULES.map(([s, n]) => <th key={s} className="text-center">{n}</th>)}
            </tr></thead>
            <tbody>
              <tr>
                <td className="pl-5 text-sm font-semibold text-thstrong"><Badge tone="good">User</Badge></td>
                {MODULES.map(([s]) => (
                  <td key={s} className="text-center">
                    <Switch data-testid={`perm-user-${s}`} checked={(perms.user || []).includes(s)} onCheckedChange={() => toggle(s)} />
                  </td>
                ))}
              </tr>
              {[["Admin", "warn"], ["Super Admin", "bad"]].map(([r, tone]) => (
                <tr key={r} className="opacity-60">
                  <td className="pl-5"><Badge tone={tone}>{r}</Badge></td>
                  {MODULES.map(([s]) => <td key={s} className="text-center"><Switch checked disabled /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------- Uploads ----------
const MONTHS_12 = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const MONTH_LABEL = { "01": "January", "02": "February", "03": "March", "04": "April", "05": "May", "06": "June",
  "07": "July", "08": "August", "09": "September", "10": "October", "11": "November", "12": "December" };
const MonthCard = ({ month, mm, m, busy, onUpload, onDelete, onRemap }) => {
  const [over, setOver] = useState(false);
  const rows = m ? Object.values(m.rows_per_tab || {}).reduce((a, b) => a + b, 0) : 0;
  const mp = m?.mapping;
  return (
    <div data-testid={`upload-month-row-${month}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onUpload(month, e.dataTransfer.files?.[0]); }}
      className={`mk-card flex flex-col p-4 transition-colors ${over ? "border-thcyan/60 bg-thcyan/5" : ""} ${m ? "" : "border-dashed"}`}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <p className="font-heading text-sm font-bold text-thstrong">{MONTH_LABEL[mm]}</p>
        {m ? <Badge tone="good" testid={`upload-status-${month}`}><CheckCircle2 className="h-2.5 w-2.5" /> Uploaded</Badge>
           : <Badge tone="neutral" testid={`upload-status-${month}`}>Missing</Badge>}
      </div>
      {m ? (
        <div className="flex-1 space-y-1">
          <p className="font-heading text-xl font-bold text-thcyan">{rows.toLocaleString()} <span className="mk-sub">rows · {Object.keys(m.rows_per_tab || {}).length} tab(s)</span></p>
          {m.date_range && <p className="mk-sub">{m.date_range.min} → {m.date_range.max}</p>}
          {(m.warnings || []).map((w, i) => <p key={i} className="mk-sub text-thamber" data-testid={`upload-warning-${month}`} title={w}>⚠ {w.split(" — ")[0]}</p>)}
          {m.repaired > 0 && <p className="mk-sub" data-testid={`upload-repaired-${month}`}>{m.repaired.toLocaleString()} dates repaired · day/month swap</p>}
          {m.backdated && <p className="mk-sub text-thblue" data-testid={`upload-backdated-${month}`}>{m.backdated.count.toLocaleString()} evaluated late · {m.backdated.months.map((x) => MONTH_LABEL[x.slice(5)].slice(0, 3)).join(", ")}</p>}
          {mp ? <p className={`mk-sub ${mp.unmapped_ids || mp.cross_ids ? "text-thamber" : "text-thmint"}`} data-testid={`upload-mapping-${month}`} title={`${mp.matched.toLocaleString()} rows matched to the Agent Mapping master${mp.cross_ids ? ` · ${mp.cross_ids} ID(s) mapped to another dashboard (${mp.cross_rows} rows excluded here)` : ""}${mp.unassigned_rows ? ` · ${mp.unassigned_rows} rows of agents without dashboard (excluded)` : ""}${m.remapped_at ? ` · re-mapped ${new Date(m.remapped_at).toLocaleString()} by ${m.remapped_by}` : ""}`}>{mp.unmapped_ids ? `${mp.unmapped_ids} ID${mp.unmapped_ids > 1 ? "s" : ""} not in master · ${mp.unmapped_rows.toLocaleString()} rows Unmapped` : "All IDs in master"}{mp.cross_ids ? ` · ${mp.cross_ids} cross-dashboard` : ""}{m.remapped_at ? " · re-mapped" : ""}</p>
              : <p className="mk-sub" data-testid={`upload-mapping-${month}`}>Stored with the dump's own Name / Location</p>}
          <p className="mk-sub">by {m.uploaded_by}{m.uploaded_at ? ` · ${new Date(m.uploaded_at).toLocaleString()}` : ""}</p>
        </div>
      ) : (
        <p className="mk-sub flex-1">Drop an .xlsx here or attach</p>
      )}
      <div className="mt-4 flex items-center gap-2">
        <label data-testid={`upload-${m ? "replace" : "attach"}-${month}`}
          className={`flex cursor-pointer items-center gap-2 ${m ? "mk-pill" : "mk-btn-primary"} !py-1.5 !text-xs`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
          {m ? "Replace" : "Attach"}
          <input type="file" accept=".xlsx" className="hidden" disabled={busy} onChange={(e) => { onUpload(month, e.target.files[0]); e.target.value = ""; }} />
        </label>
        {m && (
          <button data-testid={`upload-remap-${month}`} onClick={() => onRemap(month)} disabled={busy} title="Re-map this month from the Agent Mapping master" className="rounded-full border border-thborder/12 p-2 text-thmute transition-colors hover:border-thcyan/40 hover:text-thcyan">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        )}
        {m && (
          <button data-testid={`upload-delete-${month}`} onClick={() => onDelete(month)} className="rounded-full border border-thborder/12 p-2 text-thmute transition-colors hover:border-thcoral/40 hover:text-thcoral">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

const specSummary = (spec) => {
  if (!spec) return "Loading format…";
  const req = spec.tabs.filter((t) => t.required).map((t) => t.name);
  const opt = spec.tabs.filter((t) => !t.required).map((t) => t.name);
  const any = spec.required_any?.length ? ` · one of: ${spec.required_any.join(" / ")}` : "";
  return `One .xlsx per month${spec.any_tab ? " · any tab names accepted" : ""}. ${req.length ? `Required tabs: ${req.join(", ")}` : "No required tabs"}${any}${opt.length ? ` · Optional: ${opt.join(", ")}` : ""}.`;
};

export const UploadsPanel = () => {
  const { tenantId } = useTenant();
  const { modules } = useModules();
  const MODULES = (modules || []).filter((m) => m.slug !== "hc-forecasting").map((m) => [m.slug, m.name]);
  const [module, setModule] = useState("outbound-sales");
  const [year, setYear] = useState(new Date().getFullYear());
  const [ledger, setLedger] = useState(null);
  const [busyMonth, setBusyMonth] = useState(null);
  const [spec, setSpec] = useState(null);
  const [editing, setEditing] = useState(false);
  const moduleName = MODULES.find(([s]) => s === module)?.[1] || module;

  useEffect(() => { setSpec(null); setEditing(false); api.get(`/uploads/${module}/spec`).then((r) => setSpec(r.data)).catch(() => setSpec(null)); }, [module]);

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/uploads/${module}/ledger?tenant_id=${tenantId}`)
      .then((r) => setLedger(r.data))
      .catch((e) => { setLedger([]); toast.error(apiError(e)); });
  }, [tenantId, module]);
  useEffect(() => { setLedger(null); load(); }, [load]);

  const byMonth = Object.fromEntries((ledger || []).map((m) => [m.month, m]));

  const upload = async (month, file) => {
    if (!file) return;
    if (byMonth[month] && !window.confirm(`Replace all ${MONTH_LABEL[month.slice(5)]} ${month.slice(0, 4)} data for this module?`)) return;
    setBusyMonth(month);
    const fd = new FormData();
    fd.append("tenant_id", tenantId);
    fd.append("month", month);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/uploads/${module}`, fd);
      const total = Object.values(data.rows_per_tab).reduce((a, b) => a + b, 0);
      const bd = data.backdated ? ` · ${data.backdated.count.toLocaleString()} evaluated late (${data.backdated.months.map((m) => MONTH_LABEL[m.slice(5)].slice(0, 3)).join(", ")})` : "";
      const rp = data.repaired ? ` · ${data.repaired.toLocaleString()} dates repaired (day/month swap)` : "";
      toast.success(`${data.replaced ? "Replaced" : "Uploaded"} ${month} · ${total} rows across ${Object.keys(data.rows_per_tab).length} tab(s)${bd}${rp}`);
      (data.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
      if (data.mapping?.unmapped_ids) toast.warning(`${data.mapping.unmapped_ids} ID${data.mapping.unmapped_ids > 1 ? "s" : ""} in the ${MONTH_LABEL[month.slice(5)].slice(0, 3)} ${month.slice(0, 4)} ${moduleName} dump ${data.mapping.unmapped_ids > 1 ? "are" : "is"} not in the Agent Mapping master — their rows are kept as Unmapped. See Settings → Agent Mapping → IDs not in master.`, { duration: 12000 });
      if (data.mapping?.cross_ids) toast.warning(`${data.mapping.cross_ids} ID${data.mapping.cross_ids > 1 ? "s" : ""} in this dump ${data.mapping.cross_ids > 1 ? "are" : "is"} mapped to another dashboard — ${data.mapping.cross_rows} rows are excluded from ${moduleName}. See Settings → Agent Mapping → Cross-dashboard IDs.`, { duration: 12000 });
      load();
    } catch (e) { toast.error(apiError(e)); }
    setBusyMonth(null);
  };

  const remap = async (month) => {
    if (!window.confirm(`Re-map ${MONTH_LABEL[month.slice(5)]} ${month.slice(0, 4)} ${moduleName} from the Agent Mapping master? Name, Location, Team, TL and Manager in that month will be replaced by the master values valid on each row's date; IDs not in the master become Unmapped.`)) return;
    setBusyMonth(month);
    try {
      const { data } = await api.post(`/agent-master/remap?tenant_id=${tenantId}&module=${module}&month=${month}`);
      const matched = Object.values(data.tabs).reduce((a, t) => a + t.matched, 0);
      const crossRows = Object.values(data.tabs).reduce((a, t) => a + (t.cross_rows || 0), 0);
      toast.success(`Re-mapped ${month} · ${matched.toLocaleString()} rows matched${data.unmapped_ids ? ` · ${data.unmapped_ids} ID(s) not in master` : ""}${data.cross_ids ? ` · ${data.cross_ids} ID(s) on another dashboard (${crossRows} rows excluded here)` : ""}${data.unassigned_rows ? ` · ${data.unassigned_rows} rows of agents without dashboard excluded` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    setBusyMonth(null);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/uploads/${module}/template?v=${Date.now()}`, { responseType: "blob", headers: { "Cache-Control": "no-cache" } });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mieruka-${module}-template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (month) => {
    if (!window.confirm(`Delete all ${module} data for ${month}? Dashboards will lose that month.`)) return;
    try {
      await api.delete(`/uploads/${module}?tenant_id=${tenantId}&month=${month}`);
      toast.success(`Deleted ${month}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PanelHead title="Data Uploads" sub={module === "quality"
        ? "One Excel file per calendar month, organised by interaction month (Local Start Time). Evaluations may be completed up to the late-eval window after that month; interactions from other months are rejected."
        : "One Excel file per module per calendar month. Files are validated on upload — rows dated outside the chosen month are rejected. Dashboards build instantly from every uploaded month."}
        right={<TenantSelect testid="uploads-tenant-select" />} />
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="uploads-module-tabs">
        {MODULES.map(([slug, name]) => (
          <button key={slug} data-testid={`uploads-module-${slug}`} onClick={() => setModule(slug)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${module === slug ? "bg-thblue text-white" : "mk-pill"}`}>
            {name}
          </button>
        ))}
      </div>
      {editing ? (
        <div className="mb-5"><TemplateEditor module={module} moduleName={moduleName} onClose={() => setEditing(false)} onSaved={(d) => setSpec(d)} /></div>
      ) : (
        <Panel testid="uploads-template-hint" className="mb-5 !p-4" title="Expected format"
          right={<div className="flex items-center gap-2">
            <button data-testid="uploads-template-edit" onClick={() => setEditing(true)} className="mk-pill !py-1.5 !text-[11px]"><SlidersHorizontal className="h-3.5 w-3.5" /> Edit format</button>
            <button data-testid="uploads-template-download" onClick={downloadTemplate} className="mk-btn-ghost !py-1.5 !text-[11px]"><Download className="h-3.5 w-3.5" /> Download template</button>
          </div>}>
          <p className="text-xs leading-relaxed text-thmute" data-testid="uploads-template-summary">{specSummary(spec)}</p>
          {spec && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {spec.tabs.map((t) => <span key={t.name} className={`rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase tracking-[0.1em] ${t.required ? "bg-thcyan/15 text-thcyan" : "bg-thhover/8 text-thdim"}`}>{t.name} · {t.columns.length} cols</span>)}
            </div>
          )}
        </Panel>
      )}
      <div className="mb-4 flex items-center gap-3">
        <button data-testid="uploads-year-prev" onClick={() => setYear((y) => y - 1)} className="mk-pill !px-3">←</button>
        <span className="font-heading text-lg font-extrabold text-thstrong" data-testid="uploads-year-label">{year}</span>
        <button data-testid="uploads-year-next" onClick={() => setYear((y) => y + 1)} className="mk-pill !px-3">→</button>
        {ledger && <span className="mk-sub ml-2">{ledger.filter((l) => l.month.startsWith(String(year))).length} / 12 months uploaded</span>}
      </div>
      {!tenantId ? <p className="text-sm text-thdim">Create a client first.</p> : !ledger ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="mk-skel h-36" />)}</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="uploads-month-ledger">
          {MONTHS_12.map((mm) => {
            const month = `${year}-${mm}`;
            return <MonthCard key={month} month={month} mm={mm} m={byMonth[month]} busy={busyMonth === month} onUpload={upload} onDelete={del} onRemap={remap} />;
          })}
        </div>
      )}
      {tenantId && module === "inbound-voice" && <div className="mt-6 max-w-md"><VoiceTargets tenantId={tenantId} conn={{}} /></div>}
      {tenantId && module === "adherence" && <div className="mt-6 max-w-md"><AdherenceTargets tenantId={tenantId} conn={{}} /></div>}
      {tenantId && module === "quality" && <div className="mt-6 max-w-lg"><QualityTargets tenantId={tenantId} /></div>}
    </div>
  );
};

const VoiceTargets = ({ tenantId, conn }) => {
  const defaults = { aht: 300, pmt: 80, qsl: 80, voc: 70 };
  const [t, setT] = useState({ ...defaults, ...(conn.targets || {}) });
  const [saving, setSaving] = useState(false);
  const LIMITS = { aht: [1, 3600], pmt: [0, 100], qsl: [0, 100], voc: [0, 100] };
  const saveTargets = async () => {
    for (const [k, [lo, hi]] of Object.entries(LIMITS)) {
      if (t[k] < lo || t[k] > hi) { toast.error(`${k.toUpperCase()} must be between ${lo} and ${hi}`); return; }
    }
    setSaving(true);
    try {
      await api.put("/modules/inbound-voice/targets", { tenant_id: tenantId, ...t });
      toast.success("Targets saved");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  return (
    <Panel title="Module targets (drive met/miss chips)" testid="voice-targets-editor">
      <div className="mb-3 grid grid-cols-4 gap-2">
        {[["aht", "AHT (sec)"], ["pmt", "PMT %"], ["qsl", "QSL %"], ["voc", "VOC %"]].map(([k, label]) => (
          <label key={k} className="mk-sub">
            {label}
            <input data-testid={`voice-target-${k}`} type="number" min={LIMITS[k][0]} max={LIMITS[k][1]} value={t[k]}
              onChange={(e) => setT({ ...t, [k]: Number(e.target.value) })} className="mk-input mt-1 !px-2 !py-1.5 !text-xs" />
          </label>
        ))}
      </div>
      <button data-testid="voice-targets-save" onClick={saveTargets} disabled={saving} className="mk-btn-ghost !py-1.5 !text-[11px]">{saving ? "Saving…" : "Save targets"}</button>
    </Panel>
  );
};

const AdherenceTargets = ({ tenantId, conn }) => {
  const [t, setT] = useState({ hours: 9, grace: 15, ...(conn.targets || {}) });
  const [saving, setSaving] = useState(false);
  const saveTargets = async () => {
    if (t.hours < 1 || t.hours > 24) { toast.error("Required hours must be between 1 and 24"); return; }
    if (t.grace < 0 || t.grace > 120) { toast.error("Grace must be between 0 and 120 minutes"); return; }
    setSaving(true);
    try {
      await api.put("/modules/adherence/targets", { tenant_id: tenantId, ...t });
      toast.success("Targets saved");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  return (
    <Panel title="Module targets (duration bar & punctuality grace)" testid="adherence-targets-editor">
      <div className="mb-3 grid grid-cols-2 gap-2">
        {[["hours", "Required hours"], ["grace", "Grace (min)"]].map(([k, lbl]) => (
          <label key={k} className="mk-sub">
            {lbl}
            <input data-testid={`adherence-target-${k}`} type="number" value={t[k]} onChange={(e) => setT({ ...t, [k]: Number(e.target.value) })} className="mk-input mt-1 !px-2 !py-1.5 !text-xs" />
          </label>
        ))}
      </div>
      <button data-testid="adherence-targets-save" onClick={saveTargets} disabled={saving} className="mk-btn-ghost !py-1.5 !text-[11px]">{saving ? "Saving…" : "Save targets"}</button>
    </Panel>
  );
};

const QualityTargets = ({ tenantId }) => {
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/modules/quality/targets?tenant_id=${tenantId}`).then((r) => setT(r.data)).catch(() => setT({ pmt: 80, qsl: 80, excellent: 90, coaching: 70, lookback: 3, auditor_target: 50, alignment_target: 90, tat_target: 2, fatal_threshold: 5 })); }, [tenantId]);
  const saveTargets = async () => {
    if (!(t.coaching < t.pmt && t.pmt <= t.excellent)) { toast.error("Thresholds must satisfy coaching < pass target ≤ excellent"); return; }
    if (t.lookback < 0 || t.lookback > 12) { toast.error("Late-eval window must be between 0 and 12 months"); return; }
    if (!(t.defaulter_min >= 1)) { toast.error("Defaulter misses must be at least 1"); return; }
    if (!(t.gap_long >= 1 && t.gap_long <= 600) || t.gap_short < 0 || t.gap_short >= t.gap_long) { toast.error("Gap thresholds: very short must be below very long (1–600 min)"); return; }
    setSaving(true);
    try {
      await api.put("/modules/quality/targets", { tenant_id: tenantId, ...t });
      toast.success("Quality targets saved — bands update on next dashboard load");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  if (!t) return <div className="mk-skel h-28" />;
  return (
    <Panel title="Targets, bands & late-evaluation window" testid="quality-targets-editor">
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {[["pmt", "PMT pass ≥"], ["qsl", "QSL target ≥"], ["excellent", "Excellent ≥"], ["coaching", "Coaching <"], ["lookback", "Late-eval window (mo)"], ["auditor_target", "Evals / auditor / mo"], ["alignment_target", "Alignment target %"], ["tat_target", "Audit TAT target (d)"], ["fatal_threshold", "Fatal alert %"], ["defaulter_min", "Defaulter ≥ misses"], ["gap_short", "Very short gap (min)"], ["gap_long", "Very long gap (min)"]].map(([k, lbl]) => (
          <label key={k} className="mk-sub flex flex-col justify-end" title={k === "lookback" ? "Evaluations may be completed up to this many months after the interaction month (0 = same month)" : undefined}>
            {lbl}
            <input data-testid={`quality-target-${k}`} type="number" min={0} max={k === "lookback" ? 12 : k === "auditor_target" ? 1000 : k === "gap_long" ? 600 : k === "defaulter_min" ? 50 : 100} value={t[k] ?? ({ lookback: 3, auditor_target: 50, alignment_target: 90, tat_target: 2, fatal_threshold: 5, defaulter_min: 3, gap_short: 2, gap_long: 60 }[k] ?? 0)} onChange={(e) => setT({ ...t, [k]: Number(e.target.value) })} className="mk-input mt-1 !px-2 !py-1.5 !text-xs" />
          </label>
        ))}
      </div>
      <p className="mk-sub mb-1 normal-case tracking-normal">Bars are red below the coaching threshold, amber up to the PMT target and green at or above it; compliance bars use the fatal alert %. Defaulter = agent with at least the set misses on one parameter; gap thresholds drive the Audit Timing page.</p>
      <p className="mk-sub mb-3 normal-case tracking-normal">A Quality file is organised by interaction month (Local Start Time). Its evaluations may be dated up to the window months later (default 3 → a January file accepts evaluations through April). Interactions from other months or evaluations beyond the window still reject the file.</p>
      <button data-testid="quality-targets-save" onClick={saveTargets} disabled={saving} className="mk-btn-ghost !py-1.5 !text-[11px]">{saving ? "Saving…" : "Save targets"}</button>
    </Panel>
  );
};

// ---------- Activity Log ----------
const LOG_TABS = [["login", "Login", LogIn, "info"], ["deletion", "Deletion", Trash2, "bad"], ["error", "Error", ShieldAlert, "bad"], ["audit", "Audit", ScrollText, "good"], ["change", "Change", FileEdit, "warn"]];

export const ActivityLogPanel = () => {
  const [type, setType] = useState("login");
  const [logs, setLogs] = useState(null);
  const tone = LOG_TABS.find(([k]) => k === type)?.[3] || "neutral";

  useEffect(() => {
    setLogs(null);
    api.get(`/logs?type=${type}`).then((r) => setLogs(r.data)).catch((e) => toast.error(apiError(e)));
  }, [type]);

  return (
    <div>
      <PanelHead title="Activity Logs" sub="Operational metadata only — no client business data is ever stored on this platform." />
      <div className="mb-5 flex flex-wrap gap-2">
        {LOG_TABS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`log-tab-${k}`} onClick={() => setType(k)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${type === k ? "bg-thblue text-white" : "mk-pill"}`}>
            <Icon className="h-3.5 w-3.5" /> {label} logs
          </button>
        ))}
      </div>
      {!logs ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="mk-skel h-12" />)}</div> : (
        <div className="mk-card overflow-hidden" data-testid={`log-table-${type}`}>
          {logs.map((l, i) => (
            <div key={l.id} className={`mk-fade flex flex-wrap items-start gap-3 px-5 py-3.5 ${i ? "border-t border-thborder/6" : ""}`} style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-thcyan" />
              <span className="mk-sub w-40 shrink-0 pt-0.5 normal-case tracking-normal"><Clock className="mr-1 inline h-3 w-3" />{new Date(l.at).toLocaleString()}</span>
              <span className="w-40 shrink-0 truncate text-xs font-semibold text-thstrong">{l.actor}</span>
              <Badge tone={tone}>{l.action}</Badge>
              <span className="min-w-0 flex-1 truncate text-xs text-thmute">{l.detail || "—"}</span>
              <span className="mk-sub shrink-0">{l.tenant || "—"}</span>
            </div>
          ))}
          {logs.length === 0 && <p className="px-5 py-12 text-center text-sm text-thdim">No {type} entries yet</p>}
        </div>
      )}
    </div>
  );
};
